/**
 * @file OrdersBoardPage.tsx
 * @description Доска заказов и возвратов по стадиям воронки (phase10.5;
 * phase19.31 — разбор A, образец «Доска»).
 *
 * Страница — раскладка: заголовок в верхней панели, строка выбора (вкладки
 * «Заказы / Возвраты» и воронка — `SelectField`, а не нативный список),
 * полотно. Данные и перетаскивание — `useOrdersBoard`; тип доски и воронка
 * живут в адресе (`?type=orders|returns&pipelineId=`).
 *
 * Доска — исключение из общей ширины (SC55): на всю ширину экрана. От 1024 —
 * колонки с перетаскиванием и затенением края (`BoardCanvas`), уже —
 * стадии пилюлями и одна колонка на экран (`BoardMobile`).
 */

import { LayoutList, Plus, Sparkles } from "lucide-react";
import { Link, useNavigate, useParams } from "react-router";

import { useHasPermission } from "../../../auth/hooks";
import { BoardCanvas } from "../../../components/orders/board/BoardCanvas";
import { BoardMobile } from "../../../components/orders/board/BoardMobile";
import { BOARD_TYPE_META, type BoardType } from "../../../components/orders/board/board-types";
import { useFluidLayout } from "../../../components/ui/AppShell";
import { Button } from "../../../components/ui/button";
import { PageActionButton } from "../../../components/ui/cabinet-page-header-parts";
import { CabinetPageHeader } from "../../../components/ui/CabinetPageHeader";
import { EmptyState } from "../../../components/ui/EmptyState";
import { ListError, ListSkeleton } from "../../../components/ui/ListStates";
import { SelectField } from "../../../components/ui/SelectField";
import { Tabs } from "../../../components/ui/Tabs";
import { BELOW_LG_QUERY, useMediaQuery } from "../../../hooks/useMediaQuery";
import { useOrdersBoard } from "../../../hooks/useOrdersBoard";

const TYPE_TABS = (Object.keys(BOARD_TYPE_META) as BoardType[]).map((value) => ({ value, label: BOARD_TYPE_META[value].tab }));

export function OrdersBoardPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const canManageOrders = useHasPermission("SHOP_MANAGE_ORDERS");
  const canProcessReturns = useHasPermission("SHOP_PROCESS_RETURNS");
  const compact = useMediaQuery(BELOW_LG_QUERY);
  const board = useOrdersBoard(canManageOrders, canProcessReturns);
  const ordersBase = `/shop/${slug}/orders`;
  const meta = BOARD_TYPE_META[board.boardType];
  useFluidLayout();

  const ready = !board.loading && board.pipelines.length > 0;

  return (
    <div
      className="flex flex-col gap-4"
      // Колонки — на высоту экрана: прокрутка внутри колонки, а не страницы.
      style={compact ? undefined : { height: "calc(100dvh - 8rem)" }}
    >
      <CabinetPageHeader
        title={meta.title}
        subtitle={board.pipeline ? `Воронка «${board.pipeline.name}»` : undefined}
        // Переход видимой кнопкой, без «⋯» (SC67): на телефоне — строкой под шапкой.
        secondaryActions={<PageActionButton action={{ key: "registry", label: "Реестр заказов", icon: LayoutList, to: ordersBase }} />}
        action={
          board.boardType === "orders" && canManageOrders ? (
            <Button variant="brand" asChild>
              <Link to={`${ordersBase}/new`}>
                <Plus className="size-4" />
                Новый заказ
              </Link>
            </Button>
          ) : undefined
        }
      />

      <div className="flex shrink-0 flex-wrap items-end justify-between gap-3">
        <Tabs aria-label="Что на доске" items={TYPE_TABS} value={board.boardType} onChange={board.selectType} />
        {board.pipelines.length > 1 ? (
          <SelectField
            ariaLabel="Воронка"
            value={board.pipelineId}
            onChange={board.selectPipeline}
            options={board.pipelines.map((p) => ({ value: p.id, label: p.name }))}
            className="w-full sm:w-[260px]"
          />
        ) : null}
      </div>

      {board.error ? <ListError message={board.error} onRetry={board.reload} /> : null}

      {board.loading ? (
        <ListSkeleton rows={6} compact />
      ) : !ready ? (
        <EmptyState
          icon={Sparkles}
          title={meta.empty}
          description="Стадии воронки настраиваются в «Настройках» — раздел стадий заказов и возвратов."
        />
      ) : compact ? (
        <BoardMobile
          boardType={board.boardType}
          stages={board.stages}
          orders={board.orders}
          returns={board.returns}
          ordersBase={ordersBase}
        />
      ) : (
        <BoardCanvas
          board={board}
          onOrderClick={(order) => navigate(`${ordersBase}/${order.id}`)}
          onReturnClick={(ret) => navigate(`${ordersBase}/${ret.orderId}`)}
        />
      )}
    </div>
  );
}
